import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { Observable } from 'rxjs';
import { ApiService } from '../api/api.service';
import { AppVersionModel } from '../../models/version/app-version.model';

@Injectable({
  providedIn: 'root',
})
export class VersionService {
  private readonly VERSION_KEY = 'app_version';
  private readonly CRITICAL_STORAGE_KEYS = [
    'MENU_MODE_SKEY',
    'THEME_MODE_SKEY',
    'app-lang',
    'themeInitialized',
  ];

  private readonly VERSION_ENDPOINT = `${environment.apiLocations.identity}/app-config/version`;

  constructor(private apiService: ApiService) {}

  checkVersionFromBackend(): Observable<boolean> {
    return new Observable((observer) => {
      this.apiService.get<AppVersionModel>(this.VERSION_ENDPOINT).subscribe({
        next: (response) => {
          if (response.fail || !response.data) {
            console.warn('[VersionService] Invalid response from backend');
            observer.next(false);
            observer.complete();
            return;
          }

          const backendVersion = response.data.versionNumber;
          const storedVersion = localStorage.getItem(this.VERSION_KEY);

          if (storedVersion !== backendVersion) {
            console.log(
              `[VersionService] Version mismatch detected. Clearing cache...`
            );
            this.clearCacheAndReload(backendVersion);
            observer.next(true);
            observer.complete();
            return;
          }

          if (!storedVersion) {
            localStorage.setItem(this.VERSION_KEY, backendVersion);
          }

          observer.next(false);
          observer.complete();
        },
        error: (error) => {
          console.warn(
            '[VersionService] Backend version check failed, using environment version',
            error
          );
          observer.next(false);
          observer.complete();
        },
      });
    });
  }

  private clearCacheAndReload(newVersion: string): void {
    const preservedData: Record<string, string> = {};

    this.CRITICAL_STORAGE_KEYS.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value) {
        preservedData[key] = value;
      }
    });

    console.log('[VersionService] Clearing localStorage and sessionStorage...');
    localStorage.clear();
    sessionStorage.clear();

    Object.entries(preservedData).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });

    localStorage.setItem(this.VERSION_KEY, newVersion);

    if ('caches' in window) {
      caches.keys().then((names) => {
        console.log(`[VersionService] Deleting ${names.length} cache(s)...`);
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }

    console.log('[VersionService] Performing hard reload...');
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }
}
